import axios from 'axios';

export function useWatchlistsBuilderApi() {
  const subscribeToWatchlist = async teamWatchlistId => {
    await axios.patch('/user_entity_permissions/0', {
      entity_permissionable_id: teamWatchlistId,
      entity_permissionable_type: 'Watchlist',
      can_read: true,
      can_write: false
    });
  };

  const shareWatchlistToTeam = async watchlistId => {
    await axios.post('/team_entity_permissions', {
      entity_permissionable_type: 'Watchlist',
      entity_permissionable_id: watchlistId
    });
  };

  const getTeamEntityPermissions = async watchlistId => {
    const teamEntityPermissionsResponse = await axios.get('/team_entity_permissions', {
      params: {
        entity_permissionable_type: 'Watchlist',
        entity_permissionable_id: watchlistId
      }
    });

    return teamEntityPermissionsResponse.data;
  };

  const getWatchlist = async watchlistId => {
    const watchlistsResponse = await axios.get(`/data/watchlists/${watchlistId}`);

    return watchlistsResponse.data;
  };

  const getMaintainers = async watchlistId => {
    const maintainersResponse = await axios.get('/user_entity_maintainers', {
      params: {
        entity_permissionable_type: 'Watchlist',
        entity_permissionable_id: watchlistId
      }
    });

    return maintainersResponse.data.maintainers;
  };

  const updateMaintainers = async (currentMaintainers, newMaintainers, watchlistId) => {
    const maintainersToRemove = currentMaintainers.filter(m => !newMaintainers.includes(m));
    const maintainersToAdd = newMaintainers.filter(m => !currentMaintainers.includes(m));

    maintainersToRemove.forEach(async maintainer => {
      await axios.patch('/user_entity_permissions/0', {
        user_id: maintainer,
        entity_permissionable_id: watchlistId,
        entity_permissionable_type: 'Watchlist',
        can_read: true,
        can_write: false
      });
    });

    maintainersToAdd.forEach(async maintainer => {
      await axios.patch('/user_entity_permissions/0', {
        user_id: maintainer,
        entity_permissionable_id: watchlistId,
        entity_permissionable_type: 'Watchlist',
        can_read: true,
        can_write: true
      });
    });
  };

  const getTeamWatchlists = async entities => {
    const teamWatchlistsResponse = await axios.get('/data/team_entities?type=watchlists');
    return entities == 'onchain_addresses'
      ? teamWatchlistsResponse.data.filter(i => i.watchlist_type == 'onchain_addresses')
      : teamWatchlistsResponse.data.filter(watchlist => watchlist[entities].length > 0);
  };

  const getUserWatchlists = async entities => {
    const userWatchlistsResponse = await axios.get('/user_entity_permissions?entity_permissionable_type=Watchlist');
    const watchlistsResponse = await axios.get('/data/watchlists', { params: { type: entities } });

    return watchlistsResponse.data.map(w => {
      const userEntityPermission = userWatchlistsResponse.data.find(p => p.user_entity_permissionable_id == w.id);
      return {
        ...w,
        can_read: userEntityPermission.can_read,
        can_write: userEntityPermission.can_write,
        is_subscribed: userEntityPermission.can_read && !userEntityPermission.can_write
      };
    });
  };

  const getCompanies = async () => {
    const companiesResponse = await axios.get('/companies');

    return companiesResponse.data;
  };

  const getTeammates = async () => {
    const teammatesResponse = await axios.get('/teammates');

    return teammatesResponse.data.map(u => {
      return {
        ...u,
        label: u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.email
      };
    });
  };

  const createWatchlist = async watchlistPayload => {
    const response = await axios.post('/watchlists', {
      name: watchlistPayload.name,
      watchlist_type: watchlistPayload.watchlist_type,
      entities: watchlistPayload.selectedEntities || watchlistPayload.entities
    });

    const watchlistId = response.data.watchlist.id;

    if (watchlistPayload.shareWatchlist) {
      await axios.post('/team_entity_permissions', {
        entity_permissionable_type: 'Watchlist',
        entity_permissionable_id: watchlistId
      });

      if (watchlistPayload.maintainers.length > 0) {
        watchlistPayload.maintainers.forEach(async maintainer => {
          await axios.patch('/user_entity_permissions/0', {
            user_id: maintainer,
            entity_permissionable_id: watchlistId,
            entity_permissionable_type: 'Watchlist',
            can_read: true,
            can_write: true
          });
        });
      }
    }

    if (watchlistPayload.createNewsFeed) {
      await axios.post('/news_feeds', {
        name: watchlistPayload.name,
        watchlists: [watchlistId],
        hide_tweets: true,
        hide_low_quality: true,
        hide_translated_news: true
      });
    }

    return response.data.watchlist;
  };

  const updateWatchlist = async (watchlistPayload, defaultPayload, store) => {
    const watchlistId = watchlistPayload.selectedWatchlist.id;

    const response = await axios.patch(`/watchlists/${watchlistId}`, {
      name: watchlistPayload.name,
      watchlist_type: watchlistPayload.watchlist_type,
      entities: watchlistPayload.selectedEntities,
      empty: watchlistPayload.empty
    });

    if (watchlistPayload.shareWatchlist != defaultPayload.shareWatchlist) {
      if (watchlistPayload.shareWatchlist) {
        await axios.post('/team_entity_permissions', {
          entity_permissionable_type: 'Watchlist',
          entity_permissionable_id: watchlistId
        });

        await updateMaintainers(defaultPayload.maintainers, watchlistPayload.maintainers, watchlistId);
      } else {
        await store.dispatch(
          'confirm',
          "Disabling sharing will remove your team's access to this watchlist.  Do you really want to proceed?"
        );
        await axios.delete('/team_entity_permissions/0', {
          params: {
            entity_permissionable_type: 'Watchlist',
            entity_permissionable_id: watchlistId
          }
        });
      }
    } else {
      if (watchlistPayload.maintainers)
        await updateMaintainers(defaultPayload.maintainers, watchlistPayload.maintainers, watchlistId);
    }
    return response.data.watchlist;
  };

  const deleteWatchlist = async (watchlistId, store) => {
    await store.dispatch('setWatchlistAlertsModal', {
      status: true,
      payload: { watchlistId: watchlistId }
    });
  };
  const deleteFolder = async (watchlistId, store) => {
    let response = false;
    let obj = store.getters['watchlists/onchain_addresses']?.find(x => x.id == watchlistId);
    response = await store
      .dispatch('confirm', `Please confirm you want to delete this ${obj ? 'folder' : 'watchlist'}?`)
      .then(async () => {
        await axios.delete(`/watchlists/${watchlistId}`);
        await store.dispatch('watchlists/deleteWatchlistFromStore', watchlistId);
        return true;
      });
    return response;
  };
  const updateWatchlistEntity = async payload => {
    await axios.post('/watchlist_entity', payload);
  };

  const deleteWatchlistEntity = async payload => {
    await axios.delete(`/watchlist_entity/${payload.entity_id}`, { params: payload });
  };

  const deleteUserEntityPermission = async id => {
    await axios.delete('/user_entity_permissions/0', {
      params: {
        entity_permissionable_id: id,
        entity_permissionable_type: 'Watchlist'
      }
    });
  };

  return {
    subscribeToWatchlist,
    getTeamEntityPermissions,
    getWatchlist,
    getMaintainers,
    getTeamWatchlists,
    getUserWatchlists,
    getCompanies,
    getTeammates,
    createWatchlist,
    updateWatchlist,
    deleteWatchlist,
    deleteFolder,
    shareWatchlistToTeam,
    updateWatchlistEntity,
    deleteWatchlistEntity,
    deleteUserEntityPermission
  };
}
